import { z } from 'zod/v3';
import { EntitySchema } from '../../common/utils/schema/EntitySchema';
import { SystemClassifierItemSchema } from '../../systemClassifier';
import { AccessGroupSchema } from '../../accessGroup/schema/AccessGroupSchema';
import { DataObjectSubgroupDto } from './types';
import { UserBasicViewSchema } from '../../user/schema/viewDto/UserViewSchema';
import { DataObjectGroupType } from '../../dataobject/types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './DataObjectGroupSchema.translations.json';
import { createPaginationSchema } from '../../common/utils/schema/paginationSchema';
import { zCode, zDescription, zName } from '../../common/utils/schema/common';
import { errorMap } from '../../common/utils/schema/errorMap';

const { addTranslations, t } = i18nNsFactory('data-object-group-schema');
addTranslations(translations);

export const zDataObjectGroupType = z.nativeEnum(DataObjectGroupType);

export const BaseDataObjectSubgroupSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const DataObjectSubgroupSchema: z.ZodType<DataObjectSubgroupDto> =
  BaseDataObjectSubgroupSchema.extend({
    children: z.lazy(() => DataObjectSubgroupSchema.array()),
  });

export const DataObjectGroupDataSchema = z.object({
  id: z.number(),
  domain: SystemClassifierItemSchema.nullable(),
  dataProviderGroup: SystemClassifierItemSchema.nullable(),
  frequency: SystemClassifierItemSchema.nullable(),
  confidential: z.boolean().nullable(),
  responsible: UserBasicViewSchema.nullable(),
  accessGroup: AccessGroupSchema.nullable(),
  sources: z.array(SystemClassifierItemSchema),
});

export const BaseDataObjectGroupSchema = EntitySchema.extend({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  groupType: zDataObjectGroupType,
  systemic: z.boolean(),
  subgroups: z.array(DataObjectSubgroupSchema),
  dataObjectGroupData: DataObjectGroupDataSchema.nullable(),
  externalCode: z.string().nullable(),
});

export const DataObjectGroupSchema = BaseDataObjectGroupSchema.extend({
  responsible: UserBasicViewSchema.nullable(),
});

export const DataObjectGroupPageSchema = createPaginationSchema(
  BaseDataObjectGroupSchema,
);

export const DataObjectGroupOptionSchema = BaseDataObjectGroupSchema.transform(
  (item) => ({
    value: item.id,
    label: item.name,
    item: item,
  }),
);

export const DataObjectGroupOptionsPageSchema = createPaginationSchema(
  DataObjectGroupOptionSchema,
);

export const DataObjectGroupValidFormDataSchema = z.object({
  groupType: z.nativeEnum(DataObjectGroupType, {
    errorMap: () => ({
      message: t('validation.groupType.required'),
    }),
  }),
  code: zCode(errorMap(() => t('validation.code.required'))).min(2),
  name: zName(errorMap(() => t('validation.name.required'))).min(1),
  description: zDescription().nullish(),
});

export const GroupDescriptionSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string().nullish(),
  type: z.string(),
});

export const GroupDescriptionPageSchema = createPaginationSchema(
  GroupDescriptionSchema,
);
